import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

import { Badge } from '@client/components/ui/badge';
import { Button } from '@client/components/ui/button';
import { CurrencyInput, PercentInput } from '@client/components/NumericInput';
import { cn } from '@client/lib';
import { formatCurrency } from '@shared';

import { type AssetTypeId } from '@/features/guided-financial-planning/config';
import { useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { useSelfFundingForm } from '@/features/guided-financial-planning/hooks';

type SaveState = 'idle' | 'saving' | 'saved';

function LoadingOverlay({ state }: { state: SaveState }) {
  if (state === 'idle') return null;

  return (
    <div
      className={cn(
        'absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm transition-opacity duration-500',
        state === 'saved' ? 'opacity-0' : 'opacity-100',
      )}
    >
      {state === 'saving' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <p className="text-sm font-medium text-gray-700">Saving...</p>
        </div>
      )}
      {state === 'saved' && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
            <CheckIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-sm font-medium text-green-600">Saved</p>
        </div>
      )}
    </div>
  );
}

interface AccountCardProps {
  assetTypeId: AssetTypeId;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  fundingSourceId: string | null;
  existingSource?: SelfFundingSource;
  onSaveComplete: () => void;
  disabled?: boolean;
  allocationAmount?: number;
}

function isSourceComplete(source?: SelfFundingSource): boolean {
  if (!source) return false;
  return (
    source.existingAssetsValue !== null &&
    source.annualRateOfReturn !== null &&
    source.gainsTaxRate !== null
  );
}

export function AccountCard({
  assetTypeId,
  label,
  isExpanded,
  onToggle,
  fundingSourceId,
  existingSource,
  onSaveComplete,
  disabled,
  allocationAmount,
}: AccountCardProps) {
  const isComplete = isSourceComplete(existingSource);
  const { form, onSubmit, isSaving } = useSelfFundingForm(
    assetTypeId,
    fundingSourceId,
    existingSource,
  );
  const isSessionComplete = useIsSessionComplete();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const prevIsSavingRef = useRef(false);
  const saveCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track save state transitions
  useEffect(() => {
    const prevIsSaving = prevIsSavingRef.current;

    if (!prevIsSaving && isSaving) {
      setSaveState('saving');
    } else if (prevIsSaving && !isSaving && saveState === 'saving') {
      setSaveState('saved');

      saveCompleteTimeoutRef.current = setTimeout(() => {
        setSaveState('idle');
        onSaveComplete();
      }, 800);
    }

    prevIsSavingRef.current = isSaving;
  }, [isSaving, saveState, onSaveComplete]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveCompleteTimeoutRef.current) {
        clearTimeout(saveCompleteTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = useCallback(() => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-gray-900">{label}</span>
        </div>

        <div className="flex items-center gap-3">
          {isComplete && existingSource?.existingAssetsValue != null && (
            <Badge
              variant="secondary"
              className="font-medium"
            >
              {formatCurrency(existingSource.existingAssetsValue)}
              {isSessionComplete && allocationAmount && allocationAmount > 0 && (
                <span className="ml-1 text-gray-500">(−{formatCurrency(allocationAmount)})</span>
              )}
            </Badge>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-gray-100"
          >
            <div className="relative px-5 pb-5 pt-4">
              <LoadingOverlay state={saveState} />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Controller
                  name="existingAssetsValue"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <CurrencyInput
                      label="CURRENT VALUE"
                      placeholder="e.g. $50,000"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name="yearlyContribution"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <CurrencyInput
                      label="FUTURE CONTRIBUTIONS (YEARLY)"
                      placeholder="e.g. $6,000"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name="annualRateOfReturn"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <PercentInput
                      label="EXPECTED RATE OF RETURN"
                      placeholder="e.g. 7%"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />

                <Controller
                  name="gainsTaxRate"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <PercentInput
                      label="CAPITAL GAINS TAX RATE"
                      placeholder="e.g. 15%"
                      value={field.value ?? null}
                      onChange={field.onChange}
                      errorMessage={fieldState.error?.message}
                      disabled={disabled}
                    />
                  )}
                />
              </div>

              {!disabled && (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="mt-6 w-full rounded-full"
                >
                  Save
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
